import * as THREE from "three";
import type { VehicleDef } from "@hyperlight/shared";

/** Source-controlled authored enter animations. Missing or invalid assets fall
 * back to the procedural mount without blocking vehicle gameplay. */
export const VEHICLE_MOUNT_ANIMATION_URLS: Readonly<Record<string, string>> = {
  "scout-speeder": "/anim/interactions/vehicle/scout-speeder-get-on.hlanim.json",
};

/** Authored EXIT animations. Same contract as the mount: a missing entry falls
 * back to the procedural arc, which is what every other chassis still uses. */
export const VEHICLE_DISMOUNT_ANIMATION_URLS: Readonly<Record<string, string>> = {
  "scout-speeder": "/anim/interactions/vehicle/scout-speeder-get-off.hlanim.json",
};

/** Authored FAST exits — the rider throws themselves clear of a moving chassis.
 * A chassis without one falls back to its ordinary step-off, which reads badly
 * at speed but never breaks the handoff. */
export const VEHICLE_BAIL_ANIMATION_URLS: Readonly<Record<string, string>> = {
  "scout-speeder": "/anim/interactions/vehicle/scout-speeder-bail.hlanim.json",
};

/** Extract the semantic socket portion of a lab constraint reference. */
export function vehicleConstraintSocket(target: string): string | null {
  if (!target.startsWith("context:")) return null;
  const reference = target.slice("context:".length);
  const slash = reference.indexOf("/");
  return slash >= 0 ? reference.slice(slash + 1) : reference;
}

/** Resolve the same semantic sockets exposed by Animation Lab, without
 * depending on ProceduralSpeederView child names. */
export function vehicleSocketLocal(
  def: VehicleDef,
  socket: string,
  out: THREE.Vector3,
): boolean {
  if (socket === "origin") {
    out.set(0, 0, 0);
    return true;
  }
  for (const seat of def.seats) {
    if (socket === `${seat.id}-seat`) out.set(...seat.position);
    else if (socket === `${seat.id}-exit`) out.set(...seat.exit);
    else if (socket === `${seat.id}-entry` && seat.transition) out.set(...seat.transition.entry);
    else if (seat.riderPose) {
      const rider = seat.riderPose;
      const offset = socket === `${seat.id}-hand-l` ? rider.leftHand
        : socket === `${seat.id}-hand-r` ? rider.rightHand
          : socket === `${seat.id}-foot-l` ? rider.leftFoot
            : socket === `${seat.id}-foot-r` ? rider.rightFoot
              : null;
      if (!offset) continue;
      out.set(
        seat.position[0] + offset[0],
        seat.position[1] + offset[1],
        seat.position[2] + offset[2],
      );
    } else continue;
    return true;
  }
  const hardpoint = def.hardpoints.find((candidate) => candidate.id === socket);
  if (!hardpoint) return false;
  out.set(...hardpoint.position);
  return true;
}

/** Resolve an authored target offset through a live rendered chassis pose. */
export function vehicleConstraintWorldTarget(
  def: VehicleDef,
  target: string,
  offset: THREE.Vector3,
  vehiclePosition: THREE.Vector3,
  vehicleRotation: THREE.Quaternion,
  out: THREE.Vector3,
): boolean {
  const socket = vehicleConstraintSocket(target);
  if (!socket || !vehicleSocketLocal(def, socket, out)) return false;
  out.add(offset).applyQuaternion(vehicleRotation).add(vehiclePosition);
  return true;
}
